import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, UserPlus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

// Buscador de personas ya usadas en el sistema (ops_cache/liderazgo_cache/
// nm_cache) — reemplaza el texto libre en "Asignar responsable" para evitar
// errores de tecleo/duplicados como el de NM 57 (03/09). Si no hay
// coincidencia, permite usar el texto escrito tal cual (persona nueva).
const MAX_RESULTS = 50;

export function PersonaCombobox({ personas = [], value, onSelect, placeholder = 'Buscar persona…', testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? personas.filter(p => p.toLowerCase().includes(q)) : personas;
    return list.slice(0, MAX_RESULTS);
  }, [personas, search]);

  const exactMatch = useMemo(
    () => personas.some(p => p.toLowerCase() === search.trim().toLowerCase()),
    [personas, search]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-7 text-xs w-full flex items-center justify-between rounded-md border px-2 bg-transparent"
          style={{ borderColor: 'var(--vz-border-subtle)', color: value ? 'var(--vz-text-primary)' : 'var(--vz-text-muted)' }}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown size={12} className="ml-1 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Nombre y apellidos…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="text-xs py-3 px-3">
              {search.trim() ? 'Sin coincidencias — usa el botón de abajo para un nombre nuevo' : 'Escribe para buscar'}
            </CommandEmpty>
            <CommandGroup>
              {results.map(p => (
                <CommandItem
                  key={p}
                  value={p}
                  onSelect={() => { onSelect(p); setOpen(false); setSearch(''); }}
                  className="text-xs"
                  data-testid={`${testId}-option-${p}`}
                >
                  <Check size={12} className={cn('mr-1.5', value === p ? 'opacity-100' : 'opacity-0')} />
                  {p}
                </CommandItem>
              ))}
              {search.trim() && !exactMatch && (
                <CommandItem
                  value={`__new__${search}`}
                  onSelect={() => { onSelect(search.trim()); setOpen(false); setSearch(''); }}
                  className="text-xs italic"
                  data-testid={`${testId}-use-new-name`}
                >
                  <UserPlus size={12} className="mr-1.5 flex-shrink-0" />
                  Usar «{search.trim()}» (persona nueva)
                </CommandItem>
              )}
            </CommandGroup>
            {personas.length > MAX_RESULTS && results.length === MAX_RESULTS && (
              <p className="text-[10px] text-center py-1" style={{ color: 'var(--vz-text-muted)' }}>
                Mostrando {MAX_RESULTS} de {personas.length} — refina la búsqueda
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
